import { useState, useEffect } from 'react';
import { getBackendUrl } from '@/utils/environment';

export interface RuntimeCheck {
  available: boolean;
  version?: string;
  executable?: string;
}

export interface RuntimeAvailability {
  javaScript?: RuntimeCheck;
  python?: RuntimeCheck;
  loading: boolean;
  error?: string;
}

// Cache the result to avoid repeated API calls
let cachedResult: RuntimeAvailability | null = null;
let fetchPromise: Promise<RuntimeAvailability> | null = null;

async function fetchRuntimes(): Promise<RuntimeAvailability> {
  try {
    const backendUrl = getBackendUrl();
    const response = await fetch(`${backendUrl}/api/system/runtimes`);

    if (!response.ok) {
      throw new Error(`Failed to fetch runtimes: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      javaScript: data.javaScript,
      python: data.python,
      loading: false,
    };
  } catch (error) {
    console.error('Failed to fetch runtime availability:', error);
    return {
      loading: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Hook to get runtime availability from the backend
 * Results are cached to avoid repeated API calls
 */
export function useRuntimeAvailability(): RuntimeAvailability {
  const [availability, setAvailability] = useState<RuntimeAvailability>(
    cachedResult || { loading: true }
  );

  useEffect(() => {
    // If we have cached result, use it
    if (cachedResult) {
      setAvailability(cachedResult);
      return;
    }

    // If a fetch is already in progress, wait for it
    if (fetchPromise) {
      fetchPromise.then((result) => {
        setAvailability(result);
      });
      return;
    }

    // Start a new fetch
    fetchPromise = fetchRuntimes();
    fetchPromise.then((result) => {
      cachedResult = result;
      fetchPromise = null;
      setAvailability(result);
    });
  }, []);

  return availability;
}

/**
 * Check if a specific language runtime is available
 */
export function isLanguageAvailable(
  availability: RuntimeAvailability,
  language: string
): boolean {
  if (availability.loading) return true; // Assume available while loading

  switch (language.toLowerCase()) {
    case 'javascript':
      return availability.javaScript?.available ?? true;
    case 'python':
      return availability.python?.available ?? false;
    default:
      return true;
  }
}

/**
 * Clear the cached runtime availability (useful for refresh)
 */
export function clearRuntimeCache(): void {
  cachedResult = null;
  fetchPromise = null;
}
